package one.healthium.insights.ui.personal_mealplan

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import loco.CONTENT_PERSONAL_MEAL_PLAN_BREAKFAST
import loco.CONTENT_PERSONAL_MEAL_PLAN_CUSTOMIZED_BADGE
import loco.CONTENT_PERSONAL_MEAL_PLAN_DAY_TITLE_FORMAT
import loco.CONTENT_PERSONAL_MEAL_PLAN_DESCRIPTION
import loco.CONTENT_PERSONAL_MEAL_PLAN_DINNER
import loco.CONTENT_PERSONAL_MEAL_PLAN_DRINK
import loco.CONTENT_PERSONAL_MEAL_PLAN_LUNCH
import loco.CONTENT_PERSONAL_MEAL_PLAN_SNACK
import loco.CONTENT_PERSONAL_MEAL_PLAN_TITLE
import one.healthium.insights.domain.models.IngredientModel
import one.healthium.insights.domain.models.MealPlanDayModel
import one.healthium.insights.domain.models.NutritionModel
import one.healthium.insights.domain.models.RecipeModel
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun MealPlanCard(
    modifier: Modifier = Modifier,
    dayIndex: Int,
    day: MealPlanDayModel,
    onRecipeClicked: (RecipeModel, String) -> Unit,
    onLogMeal: (RecipeModel) -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp)
            .clip(RoundedCornerShape(24.dp))
            .background(AppTheme.colors.surface)
            .padding(horizontal = 12.dp, vertical = 24.dp),
    ) {
        // Badge
        Text(
            modifier = Modifier
                .clip(RoundedCornerShape(6.dp))
                .background(AppTheme.colors.windowBackground)
                .padding(horizontal = 12.dp, vertical = 6.dp),
            text = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_CUSTOMIZED_BADGE),
            style = AppTheme.textStyles.secondary_16_400,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(12.dp))

        // Title
        Text(
            text = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_TITLE),
            style = AppTheme.textStyles.poppins26Bold,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(12.dp))

        // Description
        Text(
            text = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_DESCRIPTION),
            style = AppTheme.textStyles.secondary_16_400,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(24.dp))

        HorizontalDivider(color = AppTheme.colors.divider)

        Spacer(modifier = Modifier.height(24.dp))

        // Day title
        Text(
            text = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_DAY_TITLE_FORMAT, dayIndex + 1),
            style = AppTheme.textStyles.secondary_20_700,
            color = AppTheme.colors.text100,
        )

        Spacer(modifier = Modifier.height(12.dp))

        // Meal sections
        val breakfastLabel = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_BREAKFAST)
        val lunchLabel = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_LUNCH)
        val dinnerLabel = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_DINNER)
        val snackLabel = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_SNACK)
        val drinkLabel = stringResource(loco.Res.string.CONTENT_PERSONAL_MEAL_PLAN_DRINK)

        val sections = listOf(
            breakfastLabel to day.breakfast,
            lunchLabel to day.lunch,
            dinnerLabel to day.dinner,
            snackLabel to day.snack,
            drinkLabel to day.drink,
        )

        sections.fastForEach { (label, recipes) ->
            if (recipes.isNotEmpty()) {
                MealSection(
                    label = label,
                    recipes = recipes,
                    onRecipeClicked = onRecipeClicked,
                    onLogMeal = onLogMeal,
                )
                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}

// --- Previews ---

@Preview(showBackground = true)
@Composable
private fun MealPlanCardPreview() {
    AppTheme {
        MealPlanCard(
            dayIndex = 2,
            day = previewDay(),
            onRecipeClicked = { _, _ -> },
            onLogMeal = {},
        )
    }
}

private fun previewRecipe(
    title: String = "Garlic Chili Scrambled Eggs",
    emoji: String = "\uD83C\uDF73",
) = RecipeModel(
    id = "preview",
    title = title,
    emoji = emoji,
    servingWeightGrams = 228,
    ingredients = listOf(
        IngredientModel(name = "eggs", emoji = "\uD83E\uDD5A", amount = 4.0, unit = "pcs"),
        IngredientModel(name = "butter", emoji = "\uD83E\uDDC8", amount = 1.0, unit = "tbsp"),
    ),
    steps = listOf("Step 1", "Step 2"),
    nutritionPer100g = NutritionModel(calories = 185.0, protein = 11.7, fat = 14.6, carbs = 2.1),
)

private fun previewDay() = MealPlanDayModel(
    totalCalories = 1748,
    breakfast = listOf(previewRecipe()),
    lunch = listOf(previewRecipe(title = "Beef Patties", emoji = "\uD83E\uDD69")),
    dinner = listOf(previewRecipe(title = "Chicken Thighs", emoji = "\uD83C\uDF57")),
    snack = listOf(previewRecipe(title = "Cheddar Cheese", emoji = "\uD83E\uDDC0")),
    drink = emptyList(),
)
